import React from "react"
import type { UserDocument } from "popopo-msg"
import { firestore } from "../core"
import { Navigate } from "react-router-dom"
import { useLive, useSelection, useSelectionSequences, useSpace, useSpaceUser } from "popopo-lib-front/firestore-access"
import { SelectionParticipantCreateForm } from "../component/selection/SelectionParticipantCreateForm"
import { useAccessContext } from "../component/guard/AccessGuard"
import { SelectionSequenceParticipantForm } from "../component/selection/SelectionSequenceParticipantForm"
import { SelectionNominateForm } from "../component/selection/SelectionNominate"
import { SelectionNominateList } from "../component/selection/SelectionNominateList"
import { SelectionParticipantListForGuest, SelectionParticipantListForSpaceUser } from "../component/selection/SelectionParticipantList"
import { SelectionFinishForm } from "../component/selection/SelectionFinishForm"
import { SpinnerRow } from "../component/SpinnerRow"
import { SelectionSpaceConnectionForm } from "../component/selection/SelectionSpaceConnectionForm"
import { Paths } from "../paths"
import { usePathParams } from "popopo-lib-front/hook/path"

function getLookDisplayId(look: UserDocument["look"] | null): string {
  if (look === undefined || look === null) return "-"
  return look.id
}

export const Selection = () => {
  const context = useAccessContext()
  const { spaceKey, liveId, selectionId } = usePathParams(Paths.spaces.space.lives.live.selections.selection)
  const space = useSpace(firestore, spaceKey)
  const spaceUser = useSpaceUser(firestore, spaceKey, context.user.id)
  const live = useLive(firestore, spaceKey, liveId)
  const selection = useSelection(firestore, spaceKey, liveId, selectionId)
  const sequences = useSelectionSequences(firestore, spaceKey, liveId, selectionId)

  if (space === null || live === null || selection === null) {
    return <Navigate to={Paths.top.path}/>
  }

  if (space === "loading" || live === "loading" || selection === "loading" || sequences === "loading") {
    return <SpinnerRow/>
  }

  const latestSequence = sequences.length === 0 ? null : sequences[0]

  return (<>
    <div className="row">
      <div className="col-12 offset-lg-3 col-lg-6 d-flex flex-column align-items-center gap-3">
        <h3>Selection</h3>
        <div>
          <div>ID: {selection.id}</div>
          <div>Status: {selection.status}</div>
          <div>Kind: {selection.kind}</div>
          <div>Title: {selection.title}</div>
          <div>Count: {selection.counts.participants}</div>
          <div>
            Nominates:{" "}
            {selection.current_nominates.map((x) => (
              <li key={x.id}>
                {x.id} (UID: {x.user.id}, {x.selected}, {x.appeal?.kind ?? "None"})
              </li>
            ))}
          </div>
          <div>Created at: {new Date(selection.created_at).toLocaleString()}</div>
          <div>Updated at: {new Date(selection.updated_at).toLocaleString()}</div>
        </div>

        {space.selections && (<>
          <h3>Selection info (denormalized)</h3>
          {space.selections.map(selection => <div key={selection.id}>
            <div>ID: {selection.id}</div>
            <div>Kind: {selection.kind}</div>
            <div>Status: {selection.status}</div>
            <div>UID: {selection.user_id}</div>
            <div>Title: {selection.title}</div>
            {selection.selected_participant && (<>
              <h5>Selected participant</h5>
              <div>ID: {selection.selected_participant.id}</div>
              <div>Status: {selection.selected_participant.status}</div>
              <div>UID: {selection.selected_participant.user.id}</div>
              <div>Alias: {selection.selected_participant.user.alias}</div>
              <div>Name: {selection.selected_participant.user.name}</div>
              <div>Icon: {selection.selected_participant.user.icon}</div>
              <div>Look ID: {getLookDisplayId(selection.selected_participant.user.look)}</div>
              <div>Look kind: {selection.selected_participant.user.look?.kind}</div>
              <div>Look name: {selection.selected_participant.user.look?.name}</div>
              <div>Look icon: {selection.selected_participant.user.look?.icon}</div>
            </>)}
            {selection.last_selected_participant && (<>
              <h5>Last selected participant</h5>
              <div>ID: {selection.last_selected_participant.id}</div>
              <div>Status: {selection.last_selected_participant.status}</div>
              <div>UID: {selection.last_selected_participant.user.id}</div>
              <div>Alias: {selection.last_selected_participant.user.alias}</div>
              <div>Name: {selection.last_selected_participant.user.name}</div>
              <div>Icon: {selection.last_selected_participant.user.icon}</div>
              <div>Look ID: {getLookDisplayId(selection.last_selected_participant.user.look)}</div>
              <div>Look kind: {selection.last_selected_participant.user.look?.kind}</div>
              <div>Look name: {selection.last_selected_participant.user.look?.name}</div>
              <div>Look icon: {selection.last_selected_participant.user.look?.icon}</div>
              {selection.last_selected_participant.selected_at && (
                <div>Selected at: {new Date(selection.last_selected_participant.selected_at).toLocaleString()}</div>
              )}
            </>)}
          </div>)}
        </>)}
      </div>
    </div>

    <div className="row">
      <div className="col-12 col-lg-6 d-flex flex-column align-items-center gap-3">
        <h3>Participate</h3>

        <SelectionSequenceParticipantForm firebase={context.credential.user} spaceKey={spaceKey} liveId={liveId} selectionId={selectionId}
                                          sequence={latestSequence?.id ?? null}/>

        <SelectionParticipantCreateForm firebase={context.credential.user} spaceKey={spaceKey} liveId={liveId} selectionId={selectionId}/>

        <h3>Participants</h3>
        {spaceUser === "loading" ?
          <p>読込中</p>
          : spaceUser
            ? <SelectionParticipantListForSpaceUser firebase={context.credential.user} spaceKey={spaceKey} liveId={liveId} selectionId={selectionId}/>
            : <SelectionParticipantListForGuest firebase={context.credential.user} spaceKey={spaceKey} liveId={liveId} selectionId={selectionId}
                                                userId={context.user.id}/>
        }
      </div>

      <div className="col-12 col-lg-6 d-flex flex-column align-items-center gap-3">
        <h3>Nominate</h3>
        <SelectionNominateForm firebase={context.credential.user} spaceKey={spaceKey} liveId={liveId} selectionId={selectionId}
                               sequence={latestSequence?.id ?? null}/>

        <h3>Sequences</h3>
        <ul className="list-group w-100">
          {sequences.length === 0 ? (
            <li>シーケンスは1つも存在しません。</li>
          ) : (
            sequences.map((sequence) => (
              <li key={sequence.id} className="list-group-item">
                ID: {sequence.id} (kind: {sequence.kind})
                {sequence.kind === "participate" ? (
                  `Open: ${sequence.value.open}`
                ) : (
                  <>
                  </>
                )}
              </li>
            ))
          )}
        </ul>

        <h3>Nominates</h3>
        <SelectionNominateList
          firebase={context.credential.user}
          spaceKey={spaceKey}
          liveId={liveId}
          selectionId={selectionId}
          latestSequence={latestSequence?.id ?? null}
          kind={"nominate"}
        />

        <h3>Pseudo Nominates</h3>
        <SelectionNominateList
          firebase={context.credential.user}
          spaceKey={spaceKey}
          liveId={liveId}
          selectionId={selectionId}
          latestSequence={latestSequence?.id ?? null}
          kind={"pseudo-nominate"}
        />
      </div>
    </div>

    <div className="row">
      <div className="col-12 col-lg-6 d-flex flex-column align-items-center gap-3">
        <h3>Connection</h3>

        <SelectionSpaceConnectionForm firebase={context.credential.user} spaceKey={space.id} liveId={live.id} selectionId={selection.id}/>
      </div>
    </div>

    <div className="row gy-5 justify-content-center">
      <div className="col-12 col-lg-6 d-flex flex-column align-items-center gap-3">
        <h3>Finish selection</h3>
        <SelectionFinishForm
          firebase={context.credential.user}
          spaceKey={spaceKey}
          liveId={liveId}
          selectionId={selectionId}
        />
      </div>
    </div>
  </>)
}
